import { useState } from 'react';
import { ApiError } from '../lib/api';

/** Shared building blocks for the auth pages (login, forgot / reset password). */

export const EMAIL_RE = /^\S+@\S+\.\S+$/;

/** Mirrors the API's password policy so the form can explain it before submitting. */
export function passwordProblem(pw: string): string | null {
  if (pw.length < 8) return 'At least 8 characters';
  if (pw.length > 128) return 'At most 128 characters';
  if (!/[A-Za-z]/.test(pw) || !/\d/.test(pw)) return 'Include at least one letter and one number';
  return null;
}
export const PASSWORD_RULE = 'At least 8 characters, with a letter and a number';

/** Mirrors the API's stricter policy for setting a new password (reset or change): adds upper and lower case. */
export function strongPasswordProblem(pw: string): string | null {
  const basic = passwordProblem(pw);
  if (basic) return basic;
  if (!/[A-Z]/.test(pw)) return 'Include an uppercase letter';
  if (!/[a-z]/.test(pw)) return 'Include a lowercase letter';
  return null;
}
export const STRONG_PASSWORD_RULE = 'At least 8 characters, with upper and lower case letters and a number';

export const errorMessage = (err: unknown) =>
  err instanceof ApiError ? err.message : 'Something went wrong. Please try again.';

export function Heading({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <h1 className="text-[clamp(34px,3.4vw,46px)] font-semibold leading-[1.08] tracking-[-0.035em]">
      {title}
      <span className="block text-muted">{subtitle}</span>
    </h1>
  );
}

export function Field(props: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  autoComplete?: string;
  inputMode?: 'numeric' | 'text';
  autoFocus?: boolean;
  inputClassName?: string;
  trailing?: React.ReactNode;
  onBlur?: () => void;
  /** Shown under the field in red, and marks it invalid. */
  error?: string | null;
}) {
  return (
    <div className="relative mb-3">
      <input
        id={props.id}
        onBlur={props.onBlur}
        aria-invalid={props.error ? true : undefined}
        aria-describedby={props.error ? `${props.id}-err` : undefined}
        type={props.type ?? 'text'}
        autoComplete={props.autoComplete}
        inputMode={props.inputMode}
        autoFocus={props.autoFocus}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder=" "
        className={`peer h-14 w-full rounded-full border ${props.error ? 'border-down' : 'border-line'} bg-bg pb-1.5 pl-6 pr-13 pt-[22px] text-[15px] font-medium text-fg outline-none transition focus:border-fg focus:shadow-[0_0_0_4px_var(--accent-bg)] ${props.inputClassName ?? ''}`}
      />
      <label
        htmlFor={props.id}
        className="pointer-events-none absolute left-[25px] top-[17px] -translate-y-1/2 text-[11px] font-medium text-muted transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-[15px] peer-placeholder-shown:font-normal peer-focus:top-[17px] peer-focus:text-[11px] peer-focus:font-medium"
      >
        {props.label}
      </label>
      {props.trailing && <div className="absolute right-2.5 top-7 -translate-y-1/2">{props.trailing}</div>}
      {props.error && (
        <p id={`${props.id}-err`} role="alert" className="mx-6 mb-0 mt-1.5 text-[13px] text-down">
          {props.error}
        </p>
      )}
    </div>
  );
}

export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="mb-3 rounded-2xl bg-down-bg px-4 py-3 text-sm text-down">
      {message}
    </p>
  );
}

export function PrimaryButton(props: { disabled: boolean; busy: boolean; busyLabel: string; children: React.ReactNode }) {
  return (
    <button
      type="submit"
      disabled={props.disabled}
      aria-busy={props.busy}
      className={`flex h-14 w-full items-center justify-center gap-2.5 rounded-full bg-fg text-base font-semibold text-bg transition-transform active:scale-[.99] disabled:cursor-not-allowed disabled:active:scale-100 ${
        props.busy ? 'disabled:cursor-wait' : 'disabled:bg-raised disabled:text-muted'
      }`}
    >
      {props.busy && (
        <span className="size-[18px] animate-spin rounded-full border-2 border-current/35 border-t-current" />
      )}
      {props.busy ? props.busyLabel : props.children}
    </button>
  );
}


export function PasswordField(props: {
  id: string;
  label: string;
  autoComplete: 'current-password' | 'new-password';
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
}) {
  const [show, setShow] = useState(false);
  return (
    <Field
      {...props}
      type={show ? 'text' : 'password'}
      trailing={
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? 'Hide password' : 'Show password'}
          className="grid size-[38px] place-items-center rounded-full text-muted hover:bg-raised hover:text-fg"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {show ? (
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19M1 1l22 22" />
            ) : (
              <>
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </>
            )}
          </svg>
        </button>
      }
    />
  );
}

/** Drop-down in the same pill style as Field, its label always above the value. */
export function SelectField(props: { id: string; label: string; value: string; onChange: (v: string) => void; autoComplete?: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={`relative mb-3 ${props.className ?? ''}`}>
      <select
        id={props.id}
        value={props.value}
        autoComplete={props.autoComplete}
        onChange={(e) => props.onChange(e.target.value)}
        className={`h-14 w-full appearance-none rounded-full border border-line bg-bg pb-1.5 pl-6 pr-11 pt-[22px] text-[15px] font-medium outline-none transition focus:border-fg focus:shadow-[0_0_0_4px_var(--accent-bg)] ${props.value ? 'text-fg' : 'text-muted'}`}
      >
        {props.children}
      </select>
      <label htmlFor={props.id} className="pointer-events-none absolute left-[25px] top-[17px] -translate-y-1/2 text-[11px] font-medium text-muted">
        {props.label}
      </label>
      <svg className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-muted" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="m6 9 6 6 6-6" />
      </svg>
    </div>
  );
}
